/**
 * Smart Google Maps deep-link helper.
 *
 * Why Google Maps deep-link (not in-app turn-by-turn): Google Maps has
 * world-class routing, live traffic, voice guidance, lane assist, and
 * works offline once the route is loaded. Replicating any of that
 * inside a PWA needs a paid routing API (Mapbox / Google Directions
 * with usage billing) and a custom voice TTS engine — months of work
 * for a worse driving experience. The industry standard (Uber Eats,
 * DoorDash, Talabat, Careem) is exactly this: in-app preview + native
 * deep-link to the user's preferred maps app.
 *
 * Google Maps URL spec we use:
 *   https://www.google.com/maps/dir/?api=1
 *     &destination=LAT,LNG            (required)
 *     &travelmode=driving              (default we want)
 *     &dir_action=navigate             (skips the route-preview page
 *                                       and starts turn-by-turn
 *                                       IMMEDIATELY in the Google
 *                                       Maps app)
 *
 * On Android: Chrome resolves this to an intent and Google Maps opens
 * straight into turn-by-turn. If Google Maps isn't installed it falls
 * back to the web Maps page.
 *
 * On iOS: Safari opens Google Maps app if installed; otherwise opens
 * the web page. iOS users who prefer Apple Maps can still tap the
 * "directions" button on the web fallback.
 *
 * We deliberately DO NOT pass `origin` — Google Maps will use the
 * device's live GPS as the origin, which is far more accurate than
 * any cached coord we'd have on hand.
 */

const round = (n, p = 6) => Number.isFinite(n) ? Number(n.toFixed(p)) : null;

/**
 * Build the Google Maps deep-link URL for a destination.
 * @param {{lat:number, lng:number, label?:string}} dest
 * @returns {string|null} URL or null if dest is invalid.
 */
export const googleMapsNavUrl = (dest) => {
  if (!dest) return null;
  const lat = round(dest.lat);
  const lng = round(dest.lng);
  if (lat === null || lng === null) return null;
  // `destination` accepts "lat,lng" — no need to URL-encode the comma.
  const params = new URLSearchParams({
    api: '1',
    destination: `${lat},${lng}`,
    travelmode: 'driving',
    dir_action: 'navigate',
  });
  return `https://www.google.com/maps/dir/?${params.toString()}`;
};

/**
 * Open Google Maps navigation to the given destination. Uses a fresh
 * tab on desktop / a new browsing context on mobile so the driver
 * PWA isn't replaced.
 */
export const openGoogleMapsNav = (dest) => {
  const url = googleMapsNavUrl(dest);
  if (!url) return false;
  // `_blank` + noopener so the driver PWA stays mounted in the
  // background (especially important on iOS where killing the tab
  // would stop the watchPosition live-tracking).
  window.open(url, '_blank', 'noopener,noreferrer');
  return true;
};

/**
 * Build a Google Maps multi-stop deep-link URL.
 *
 * Google Maps URL spec:
 *   destination=LAT,LNG (required — the final stop)
 *   waypoints=LAT,LNG|LAT,LNG|...  (intermediate stops in order)
 *
 * As with the single-stop version we deliberately OMIT `origin` so
 * Google Maps uses the device's live GPS — most accurate.
 *
 * Limits: Google Maps mobile-app deep-link supports a maximum of 9
 * waypoints + 1 destination = 10 stops total. We truncate silently.
 *
 * @param {Array<{lat:number,lng:number}>} stops Ordered list of stops
 * @returns {string|null}
 */
export const googleMapsMultiStopUrl = (stops) => {
  if (!Array.isArray(stops) || stops.length === 0) return null;
  const trimmed = stops
    .map((s) => ({ lat: round(s?.lat), lng: round(s?.lng) }))
    .filter((s) => s.lat !== null && s.lng !== null)
    .slice(0, 10); // hard cap — Google won't accept more than 10 stops anyway
  if (trimmed.length === 0) return null;
  if (trimmed.length === 1) {
    return googleMapsNavUrl(trimmed[0]);
  }
  const destination = trimmed[trimmed.length - 1];
  const waypoints = trimmed.slice(0, -1);
  const params = new URLSearchParams({
    api: '1',
    destination: `${destination.lat},${destination.lng}`,
    travelmode: 'driving',
    dir_action: 'navigate',
  });
  // URLSearchParams encodes the pipe as %7C — that's fine, Maps accepts it.
  params.set(
    'waypoints',
    waypoints.map((w) => `${w.lat},${w.lng}`).join('|'),
  );
  return `https://www.google.com/maps/dir/?${params.toString()}`;
};

/**
 * Open Google Maps navigation across multiple stops.
 */
export const openGoogleMapsMultiStop = (stops) => {
  const url = googleMapsMultiStopUrl(stops);
  if (!url) return false;
  window.open(url, '_blank', 'noopener,noreferrer');
  return true;
};

/**
 * Haversine distance in km between two {lat,lng} points. Used as a
 * sanity-check fallback when the OSRM route fetch fails.
 */
export const haversineKm = (a, b) => {
  if (!a || !b) return null;
  const R = 6371; // earth radius km
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const la1 = toRad(a.lat);
  const la2 = toRad(b.lat);
  const x =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(la1) * Math.cos(la2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(x));
};
